import reportListFixture from '../queryCapabilities/fixtures/report-list.json';
import { loadQueryCapabilityFixture } from '../queryCapabilities/queryCapabilities';
import type { BackofficeCanonicalReportFilterItem } from './reportsGateway';

type ReportListFilter = {
  field: string;
  op: string;
  value: string | string[];
};

type ReportListSort = {
  field: string;
  direction: string;
};

export const reportListQueryCapabilities = loadQueryCapabilityFixture(reportListFixture);

const REPORT_FILTER_FIELD_CAPABILITIES = new Map(
  reportListQueryCapabilities.fields.map((field) => [field.field, field] as const)
);

const REPORT_SORTABLE_FIELDS = new Set(reportListQueryCapabilities.sortableFields);

const REPORT_FILTER_COMBINATIONS = new Set(
  reportListQueryCapabilities.unsupportedCombinations.map(
    (combination) => `${combination.field}::${combination.operator}`
  )
);

const REPORT_MAXIMUM_FILTERS = reportListQueryCapabilities.compound.maximumFilters;

const DATE_FILTER_REGEXP = /^\d{4}-\d{2}-\d{2}$/;

const normalizeDateValue = (value: unknown): string => {
  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }

  const formatted = String(value ?? '');
  if (!DATE_FILTER_REGEXP.test(formatted)) {
    throw new Error('Date filter value must be YYYY-MM-DD');
  }

  return formatted;
};

const validateFilterOperand = (filter: ReportListFilter, operandKind: string): void => {
  if (operandKind === 'multiple') {
    if (!Array.isArray(filter.value) || filter.value.length === 0) {
      throw new Error(`Operator '${filter.op}' requires an array value`);
    }

    if (!filter.value.every((item) => typeof item === 'string' && item.length > 0)) {
      throw new Error(`Operator '${filter.op}' requires an array of strings`);
    }

    return;
  }

  if (Array.isArray(filter.value)) {
    throw new Error(`Operator '${filter.op}' does not support array values`);
  }
};

const validateFilterValueKind = (
  filter: ReportListFilter,
  valueKind?: string
): string | string[] => {
  if (valueKind === 'date') {
    return normalizeDateValue(filter.value);
  }

  return filter.value;
};

const normalizeFilterOperator = (filter: ReportListFilter): ReportListFilter => {
  if (filter.field === 'sightingDate' && filter.op === 'equals') {
    return {
      ...filter,
      op: 'onDate',
    };
  }

  return filter;
};

export const assertReportListQueryCapabilities = (
  filters: ReportListFilter[],
  sort?: ReportListSort
): void => {
  if (filters.length > REPORT_MAXIMUM_FILTERS) {
    throw new Error(`Report list supports at most ${REPORT_MAXIMUM_FILTERS} filters`);
  }

  if (sort && !REPORT_SORTABLE_FIELDS.has(sort.field)) {
    throw new Error(`Sort field '${sort.field}' is not supported for Report list`);
  }

  if (sort && sort.direction !== 'asc' && sort.direction !== 'desc') {
    throw new Error(`Invalid report sort direction: ${sort.direction}`);
  }
};

export const toCanonicalReportFilters = (
  filters: ReportListFilter[]
): BackofficeCanonicalReportFilterItem[] => {
  assertReportListQueryCapabilities(filters);

  return filters.map<BackofficeCanonicalReportFilterItem>((rawFilter) => {
    const filter = normalizeFilterOperator(rawFilter);
    const fieldCapability = REPORT_FILTER_FIELD_CAPABILITIES.get(filter.field);
    if (!fieldCapability) {
      throw new Error(`Invalid report filter field: ${filter.field}`);
    }

    const operatorCapability = fieldCapability.operators.find(
      (operator) => operator.operator === filter.op
    );
    if (!operatorCapability) {
      throw new Error(`Invalid report filter operator: ${filter.op}`);
    }

    if (REPORT_FILTER_COMBINATIONS.has(`${filter.field}::${filter.op}`)) {
      throw new Error(`Operator '${filter.op}' is not supported for field '${filter.field}'`);
    }

    validateFilterOperand(filter, operatorCapability.operand.kind);

    return {
      field: filter.field as BackofficeCanonicalReportFilterItem['field'],
      op: filter.op as BackofficeCanonicalReportFilterItem['op'],
      value: validateFilterValueKind(filter, operatorCapability.operand.valueKind),
    };
  });
};
